/**
 * Phase F11 FE-1 — proctor consent gate.
 *
 * Renders before the StartGate when the attempt's proctor profile is
 * active. Asks the applicant for camera + microphone permissions via
 * the browser's `getUserMedia` and records the consent decision via
 * the proctor SDK before allowing the flow to continue to the
 * StartGate.
 *
 * Consent semantics:
 *   * Granted → records `consent_granted` event with `{ camera: true,
 *     microphone: <bool> }` metadata and stops the local media tracks
 *     (we don't keep the stream live; later F11 FE-2 slices will
 *     re-prompt for the webcam-still capture cadence).
 *   * Denied / hardware error → records `consent_denied` with
 *     `{ reason }` so the F12 model can distinguish refusal from
 *     hardware failure. The applicant can retry; the gate doesn't
 *     auto-advance.
 *   * Browser doesn't support getUserMedia → records `consent_denied`
 *     with `{ reason: "unsupported_browser" }` and surfaces a static
 *     "use a modern browser" message.
 *
 * Privacy: the local preview stream is NOT uploaded by this gate.
 * It exists purely as visual confirmation that the camera is working;
 * the periodic webcam-still capture (F11 FE-2) is what actually
 * uploads stills to the BE.
 */
import * as React from "react";
import { Button } from "@eduviaos/ui-kit";
import { motion } from "motion/react";
import { Camera, Loader2, Mic, ShieldCheck, ShieldX, AlertTriangle } from "lucide-react";

import { PageCard } from "@/components/AppFrame";
import type { ProctorClient } from "@/lib/proctor-client";

/** Local consent UI state. */
type ConsentState =
  | { kind: "idle" }
  | { kind: "requesting" }
  | { kind: "granted"; cameraOk: boolean; microphoneOk: boolean }
  | { kind: "denied"; reason: ConsentDeniedReason };

type ConsentDeniedReason =
  | "permission_denied"
  | "hardware_unavailable"
  | "unsupported_browser"
  | "transient_error";

const _DENIED_COPY: Record<ConsentDeniedReason, string> = {
  permission_denied:
    "We couldn't access your camera or microphone. Please grant permission in your browser, then try again.",
  hardware_unavailable:
    "We couldn't find a working camera or microphone. Plug one in (or pick a different device in your browser settings) and try again.",
  unsupported_browser:
    "Your browser doesn't support the proctoring requirements for this assessment. Please use the latest Chrome, Edge, Safari, or Firefox.",
  transient_error:
    "Something went wrong while requesting permission. Please try again — if the problem persists, restart the browser.",
};

export interface ProctorConsentGateProps {
  client: ProctorClient;
  templateLabel?: string;
  onConsentGranted: () => void;
}

export function ProctorConsentGate({
  client,
  templateLabel,
  onConsentGranted,
}: ProctorConsentGateProps) {
  const [state, setState] = React.useState<ConsentState>({ kind: "idle" });
  const previewRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  // Stop tracks on unmount so we never leak a hot camera if the parent
  // navigates away.
  React.useEffect(() => {
    return () => {
      const s = streamRef.current;
      if (s) {
        for (const track of s.getTracks()) track.stop();
        streamRef.current = null;
      }
    };
  }, []);

  const requestConsent = React.useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      void client.recordEvent("consent_denied", { reason: "unsupported_browser" });
      setState({ kind: "denied", reason: "unsupported_browser" });
      return;
    }

    setState({ kind: "requesting" });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;

      const cameraOk = stream.getVideoTracks().length > 0;
      const microphoneOk = stream.getAudioTracks().length > 0;
      if (previewRef.current) {
        previewRef.current.srcObject = stream;
        // Avoid the autoplay error that Chromium throws on a paused
        // <video> with srcObject — explicitly play and ignore failure
        // (some sandboxed contexts need a user gesture, which we just
        // received via the consent button).
        previewRef.current.play().catch(() => undefined);
      }

      void client.recordEvent("consent_granted", {
        camera: cameraOk,
        microphone: microphoneOk,
      });
      setState({ kind: "granted", cameraOk, microphoneOk });
    } catch (err) {
      const reason = classifyMediaError(err);
      void client.recordEvent("consent_denied", { reason });
      setState({ kind: "denied", reason });
    }
  }, [client]);

  const handleProceed = React.useCallback(() => {
    // Stop the preview tracks before handing off — the F11 FE-2 slice
    // will re-prompt at capture time. We don't keep the stream open
    // longer than the consent screen.
    const s = streamRef.current;
    if (s) {
      for (const track of s.getTracks()) track.stop();
      streamRef.current = null;
    }
    onConsentGranted();
  }, [onConsentGranted]);

  return (
    <PageCard
      title="Proctored assessment"
      subtitle={
        templateLabel
          ? `Before you begin ${templateLabel}, we need to set up the proctoring requirements.`
          : "Before you begin, we need to set up the proctoring requirements."
      }
    >
      <ul className="text-foreground/90 grid gap-3 text-sm">
        <li className="flex items-start gap-2.5">
          <Camera className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Your camera will capture periodic still images during the assessment.
            <span className="text-muted-foreground block text-xs">
              Frames are stored privately and only the institution's reviewers can see them.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2.5">
          <Mic className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Microphone access is requested for the audio quality test.
            <span className="text-muted-foreground block text-xs">
              We do not record continuous audio during the assessment.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2.5">
          <ShieldCheck className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Tab switches, paste attempts, and other browser actions are logged for review.
          </span>
        </li>
      </ul>

      <ConsentBody state={state} previewRef={previewRef} />

      {state.kind === "denied" && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{_DENIED_COPY[state.reason]}</span>
        </div>
      )}

      <ConsentActions state={state} onRequest={requestConsent} onProceed={handleProceed} />
    </PageCard>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function ConsentBody({
  state,
  previewRef,
}: {
  state: ConsentState;
  previewRef: React.RefObject<HTMLVideoElement>;
}) {
  const showPreview = state.kind === "granted";
  return (
    <div className="bg-muted/40 mt-5 grid place-items-center rounded-md p-4">
      {showPreview ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-md"
        >
          <video
            ref={previewRef}
            autoPlay
            muted
            playsInline
            className="aspect-video w-full rounded border bg-black object-cover"
            aria-label="Camera preview"
          />
        </motion.div>
      ) : (
        <div
          className="text-muted-foreground flex aspect-video w-full max-w-md flex-col items-center justify-center rounded border border-dashed text-sm"
          aria-hidden
        >
          {state.kind === "requesting" ? (
            <>
              <Loader2 className="text-primary mb-2 h-6 w-6 animate-spin" />
              <span>Waiting for permission…</span>
            </>
          ) : state.kind === "denied" ? (
            <>
              <ShieldX className="mb-2 h-6 w-6" />
              <span>Permission required</span>
            </>
          ) : (
            <>
              <Camera className="mb-2 h-6 w-6" />
              <span>Camera preview will appear here</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ConsentActions({
  state,
  onRequest,
  onProceed,
}: {
  state: ConsentState;
  onRequest: () => void;
  onProceed: () => void;
}) {
  if (state.kind === "granted") {
    return (
      <div className="mt-5 flex items-center justify-end gap-2">
        <span className="text-muted-foreground mr-auto text-xs">
          Camera + microphone ready · You can continue.
        </span>
        <Button type="button" onClick={onProceed} size="lg">
          Continue
        </Button>
      </div>
    );
  }

  const requesting = state.kind === "requesting";
  const denied = state.kind === "denied";
  const unsupported = denied && state.reason === "unsupported_browser";

  return (
    <div className="mt-5 flex justify-end">
      <Button type="button" onClick={onRequest} disabled={requesting || unsupported} size="lg">
        {requesting ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            Requesting…
          </>
        ) : denied ? (
          "Try again"
        ) : (
          "Allow camera & microphone"
        )}
      </Button>
    </div>
  );
}

// ── Error classification ────────────────────────────────────────────────

function classifyMediaError(err: unknown): ConsentDeniedReason {
  if (err === null || typeof err !== "object" || !("name" in err)) {
    return "transient_error";
  }
  const name = String((err as { name: unknown }).name);
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "permission_denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "hardware_unavailable";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "transient_error";
    default:
      return "transient_error";
  }
}
